import { useHandler } from "@redotech/react-util/hook";
import { PurchaseChannel } from "@redotech/redo-model/warranties";
import { assertNever } from "@redotech/util/type";
import { useContext, useEffect, useMemo, useState } from "react";
import { SettingsContext } from "../contexts/settings";

type PurchaseChannelOption = {
  label: string;
  id: string;
  purchaseChannel: PurchaseChannel;
};

const OTHER_OPTION: PurchaseChannelOption = {
  label: "Other",
  id: "other",
  purchaseChannel: PurchaseChannel.RETAIL,
};

const DEFAULT_PURCHASE_CHANNEL_OPTIONS: PurchaseChannelOption[] = [
  {
    label: "Shopify store",
    id: "shopify-store",
    purchaseChannel: PurchaseChannel.ONLINE,
  },
];

function getPurchaseChannelLabel(
  option?: PurchaseChannelOption,
  otherText?: string,
): string {
  if (!option) {
    return "Unknown";
  }
  if (option.id === "other") {
    return `Other (${otherText ?? "Unknown"})`;
  }
  return option.label;
}

export function useRegister({
  toRetailRegistration,
  toOnlineRegistration,
  channelFilter,
}: {
  toRetailRegistration: (channelName: string) => void;
  toOnlineRegistration: (channelName: string) => void;
  channelFilter?: PurchaseChannel;
}) {
  const settings = useContext(SettingsContext);
  const [selectedPurchaseOptionId, setSelectedPurchaseOptionId] = useState<
    string | undefined
  >(undefined);
  const [otherPurchaseChannel, setOtherPurchaseChannel] = useState<string>("");

  const purchaseChannelOptions: PurchaseChannelOption[] = useMemo(() => {
    const purchaseChannels = settings?.warranties?.purchaseChannels;
    if (!purchaseChannels || purchaseChannels.length === 0) {
      return DEFAULT_PURCHASE_CHANNEL_OPTIONS;
    }
    let options = purchaseChannels.map((channel, index) => ({
      label: channel.displayName,
      id: `purchase-channel-${index}`,
      purchaseChannel: channel.purchaseChannel,
    }));
    if (settings?.warranties?.showOtherOption) {
      options.push(OTHER_OPTION);
    }
    if (channelFilter) {
      options = options.filter(
        (option) => option.purchaseChannel === channelFilter,
      );
    }
    return options;
  }, [settings?.warranties, channelFilter]);

  const selectedPurchaseOption = useMemo(() => {
    return purchaseChannelOptions.find(
      (option) => option.id === selectedPurchaseOptionId,
    );
  }, [purchaseChannelOptions, selectedPurchaseOptionId]);

  const handleSelectPurchaseOption = useHandler(
    (selectedPurchaseOption: PurchaseChannelOption | undefined) => {
      const label = getPurchaseChannelLabel(
        selectedPurchaseOption,
        otherPurchaseChannel,
      );
      const purchaseChannel =
        selectedPurchaseOption?.purchaseChannel ?? PurchaseChannel.RETAIL;
      switch (purchaseChannel) {
        case PurchaseChannel.ONLINE:
          toOnlineRegistration(label);
          break;
        case PurchaseChannel.RETAIL:
          toRetailRegistration(label);
          break;
        default:
          assertNever(purchaseChannel);
      }
    },
  );

  const handleContinue = useHandler(() => {
    if (selectedPurchaseOption) {
      handleSelectPurchaseOption(selectedPurchaseOption);
    }
  });

  const shouldAutoSelectPurchaseOption = useMemo(() => {
    return (
      purchaseChannelOptions.length === 1 &&
      purchaseChannelOptions[0].id !== OTHER_OPTION.id
    );
  }, [purchaseChannelOptions]);

  useEffect(() => {
    if (shouldAutoSelectPurchaseOption) {
      setSelectedPurchaseOptionId(purchaseChannelOptions[0].id);
      handleSelectPurchaseOption(purchaseChannelOptions[0]);
    }
  }, [
    purchaseChannelOptions,
    handleSelectPurchaseOption,
    shouldAutoSelectPurchaseOption,
  ]);

  const showLoading = shouldAutoSelectPurchaseOption;

  const continueDisabled =
    !selectedPurchaseOptionId ||
    (selectedPurchaseOptionId === OTHER_OPTION.id && !otherPurchaseChannel);

  return {
    texts: {
      merchantName: settings?.merchantName || "Unknown Merchant",
      title: "Register your product",
      subtitle: "Where did you purchase your product?",
      continueButtonText: "Continue",
    },
    continueDisabled,
    selectedPurchaseOptionId,
    setSelectedPurchaseOptionId,
    purchaseChannelOptions,
    isOtherOptionSelected: selectedPurchaseOptionId === OTHER_OPTION.id,
    otherPurchaseChannel,
    showLoading,
    setOtherPurchaseChannel,
    onContinue: handleContinue,
  };
}
